import React, { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useAuth } from '@/hooks/useAuth';
import { SavedAccount } from '@/hooks/multipleAccounts/useSavedAccounts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

type AccountAuthModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
} & (
  | { mode: 'switch'; account: SavedAccount }
  | { mode: 'add' }
);

export function AccountAuthModal(props: AccountAuthModalProps) {
  const { open, onOpenChange, mode } = props;
  const isSwitch = mode === 'switch';
  const account = isSwitch ? props.account : null;

  const navigate = useNavigate();
  const { translator } = useContext(LanguageProviderContext);
  const { signIn } = useAuth();
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const resolvedEmail = isSwitch ? account!.email : email;
  const resolvedSlug = isSwitch ? account!.slug : slug;

  const canSubmit = isSwitch
    ? Boolean(password.trim())
    : Boolean(slug.trim() && email.trim() && password.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (isSwitch && !password.trim()) {
      setAuthError(translator('Global.fillAllFields'));
      return;
    }

    if (!isSwitch && (!slug.trim() || !email.trim() || !password.trim())) {
      setAuthError(translator('Global.fillAllFields'));
      return;
    }

    setIsSubmitting(true);
    setAuthError(null);

    try {
      const { error } = await signIn(
        resolvedEmail.trim(),
        password,
        resolvedSlug.trim(),
        false,
      );

      if (error) {
        setAuthError(
          translator(isSwitch ? 'AccountSwitcher.incorrectPassword' : 'AccountSwitcher.addAccountError'),
        );
        if (isSwitch) setPassword('');
        return;
      }

      onOpenChange(false);
      navigate('/tabs', { replace: true, state: { skipOrgLoader: true } });
    } catch (error) {
      console.error('[AccountAuthModal] Sign-in error:', error);
      setAuthError(
        translator(isSwitch ? 'AccountSwitcher.switchError' : 'AccountSwitcher.addAccountError'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={
          isSwitch
            ? (e) => {
                e.preventDefault();
                passwordInputRef.current?.focus();
              }
            : undefined
        }
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {translator(isSwitch ? 'AccountSwitcher.switchTo' : 'AccountSwitcher.addAccount')}
            </DialogTitle>
            <DialogDescription>
              {isSwitch
                ? account!.orgName
                : translator('AccountSwitcher.addAccountDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!isSwitch && (
              <div className="space-y-2">
                <Label htmlFor="account-auth-slug">{translator('AccountSwitcher.organizationSlug')}</Label>
                <Input
                  id="account-auth-slug"
                  type="text"
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setAuthError(null);
                  }}
                  placeholder="my-organization"
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="account-auth-email">{translator('Global.email')}</Label>
              <Input
                id="account-auth-email"
                type="email"
                autoComplete="email"
                value={isSwitch ? account!.email : email}
                readOnly={isSwitch}
                tabIndex={isSwitch ? -1 : undefined}
                className={isSwitch ? 'bg-muted' : undefined}
                onChange={
                  isSwitch
                    ? undefined
                    : (e) => {
                        setEmail(e.target.value);
                        setAuthError(null);
                      }
                }
                placeholder="usuario@ejemplo.com"
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="account-auth-password">
                {translator(isSwitch ? 'AccountSwitcher.enterPassword' : 'Global.password')}
              </Label>
              <Input
                ref={passwordInputRef}
                id="account-auth-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setAuthError(null);
                }}
                placeholder="••••••••"
                disabled={isSubmitting}
              />
            </div>

            {authError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {translator('Global.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !canSubmit}>
              {isSubmitting
                ? translator('Global.loading')
                : translator  (isSwitch ? 'AccountSwitcher.continue' : 'AccountSwitcher.addAccount')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
